import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Modal,
  TextField,
  Stack,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Divider,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FolderIcon from "@mui/icons-material/Folder";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { styled } from "@mui/material/styles";
import moment from "moment";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import ENV from "../../../config/env";
import { useAuth } from "../../../context/AuthContext";
import { SUCURSALES_NOTICIA } from "../../../utils/noticiasHelpers";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)", clipPath: "inset(50%)",
  height: 1, overflow: "hidden", position: "absolute",
  bottom: 0, left: 0, whiteSpace: "nowrap", width: 1,
});

const IMAGE_EXT = [".jpg", ".jpeg", ".png", ".webp", ".gif"];
const PDF_EXT = [".pdf"];

const getExt = (nombre) => {
  const idx = (nombre || "").lastIndexOf(".");
  return idx === -1 ? "" : nombre.slice(idx).toLowerCase();
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileIcon = ({ nombre, url }) => {
  const ext = getExt(nombre);
  if (IMAGE_EXT.includes(ext)) {
    return <Avatar variant="rounded" src={`${ENV.API_BASE_URL}${url}`} sx={{ width: 40, height: 40 }} />;
  }
  if (PDF_EXT.includes(ext)) {
    return (
      <Avatar variant="rounded" sx={{ bgcolor: "error.light", color: "error.dark" }}>
        <PictureAsPdfIcon />
      </Avatar>
    );
  }
  return (
    <Avatar variant="rounded" sx={{ bgcolor: "grey.200", color: "text.secondary" }}>
      <InsertDriveFileIcon />
    </Avatar>
  );
};

const FORM_ARCHIVO_VACIO = { nombre: "", descripcion: "" };
const FORM_SUBCARPETA_VACIO = { id: null, nombre: "", descripcion: "" };

const plural = (n, palabra) => `${n} ${palabra}${n === 1 ? "" : "s"}`;

const normalize = (value) =>
  String(value || "").normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "").trim().toLowerCase();

const CarpetaDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = useMemo(() => {
    const role = normalize(user?.role);
    return role === "admin" || role === "gerente";
  }, [user?.role]);
  // Todas las carpetas: de ahí salen la carpeta actual, sus subcarpetas y la ruta hasta la raíz.
  const [todasCarpetas, setTodasCarpetas] = useState([]);
  const [archivos, setArchivos] = useState([]);

  const carpeta = useMemo(
    () => todasCarpetas.find((c) => String(c.id) === String(id)) || null,
    [todasCarpetas, id]
  );
  const subcarpetas = useMemo(
    () => todasCarpetas.filter((c) => String(c.padre_id) === String(id)),
    [todasCarpetas, id]
  );
  const ruta = useMemo(() => {
    const porId = new Map(todasCarpetas.map((c) => [c.id, c]));
    const padres = [];
    let padre = carpeta ? porId.get(carpeta.padre_id) : null;
    while (padre && padres.length < 20) {
      padres.unshift(padre);
      padre = porId.get(padre.padre_id);
    }
    return padres;
  }, [todasCarpetas, carpeta]);

  // Crear o editar una subcarpeta (id null = nueva).
  const [subForm, setSubForm] = useState(FORM_SUBCARPETA_VACIO);
  const [openSubcarpeta, setOpenSubcarpeta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const [openEditar, setOpenEditar] = useState(false);
  const [form, setForm] = useState({ nombre: "", descripcion: "" });
  const [saving, setSaving] = useState(false);

  // Subida: primero se elige el archivo, luego se pide nombre/descripción antes de confirmar.
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadForm, setUploadForm] = useState(FORM_ARCHIVO_VACIO);
  const [publicarAnuncio, setPublicarAnuncio] = useState(false);
  const [sucursalAnuncio, setSucursalAnuncio] = useState("Nacional");

  // Edición de un archivo ya subido.
  const [editandoArchivo, setEditandoArchivo] = useState(null);
  const [archivoForm, setArchivoForm] = useState(FORM_ARCHIVO_VACIO);

  const fetchArchivos = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`/api/documentos/carpetas/${id}/archivos`);
      setArchivos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener archivos:", err);
      setError("No se pudo cargar la carpeta.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCarpeta = async () => {
    try {
      const { data } = await axios.get("/api/documentos/carpetas");
      setTodasCarpetas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener carpeta:", err);
    }
  };

  const handleOpenNuevaSubcarpeta = () => {
    setSubForm(FORM_SUBCARPETA_VACIO);
    setOpenSubcarpeta(true);
  };

  const handleOpenEditarSubcarpeta = (sub) => {
    setSubForm({ id: sub.id, nombre: sub.nombre, descripcion: sub.descripcion || "" });
    setOpenSubcarpeta(true);
  };

  const handleGuardarSubcarpeta = async (e) => {
    e.preventDefault();
    if (!subForm.nombre.trim()) return;
    setSaving(true);
    try {
      const body = { nombre: subForm.nombre.trim(), descripcion: subForm.descripcion.trim() };
      if (subForm.id) {
        await axios.put(`/api/documentos/carpetas/${subForm.id}`, body);
      } else {
        await axios.post("/api/documentos/carpetas", { ...body, padre_id: id });
      }
      setOpenSubcarpeta(false);
      setSubForm(FORM_SUBCARPETA_VACIO);
      fetchCarpeta();
    } catch (err) {
      console.error("Error al guardar subcarpeta:", err);
      setError(err?.response?.data?.error || "No se pudo guardar la subcarpeta.");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarSubcarpeta = async (sub) => {
    if (!window.confirm(`¿Eliminar la subcarpeta "${sub.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`/api/documentos/carpetas/${sub.id}`);
      fetchCarpeta();
    } catch (err) {
      console.error("Error al eliminar subcarpeta:", err);
      setError(err?.response?.data?.error || "No se pudo eliminar la subcarpeta.");
    }
  };

  useEffect(() => {
    fetchCarpeta();
    fetchArchivos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleOpenEditar = () => {
    setForm({ nombre: carpeta?.nombre || "", descripcion: carpeta?.descripcion || "" });
    setOpenEditar(true);
  };

  const handleGuardarEdicion = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      await axios.put(`/api/documentos/carpetas/${id}`, {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
      });
      setOpenEditar(false);
      fetchCarpeta();
    } catch (err) {
      console.error("Error al actualizar carpeta:", err);
      setError("No se pudo actualizar la carpeta.");
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingFile(file);
    setUploadForm({ nombre: file.name, descripcion: "" });
    setPublicarAnuncio(true);
    setSucursalAnuncio("Nacional");
  };

  const handleConfirmarSubida = async (e) => {
    e.preventDefault();
    if (!pendingFile || !uploadForm.nombre.trim()) return;

    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("archivo", pendingFile);
      fd.append("nombre", uploadForm.nombre.trim());
      fd.append("descripcion", uploadForm.descripcion.trim());
      await axios.post(`/api/documentos/carpetas/${id}/archivos`, fd);

      if (publicarAnuncio) {
        const fdNoticia = new FormData();
        fdNoticia.append("titulo", uploadForm.nombre.trim());
        fdNoticia.append(
          "contenido",
          uploadForm.descripcion.trim() || `Se agregó el archivo "${uploadForm.nombre.trim()}" a la carpeta "${carpeta?.nombre || ""}".`
        );
        fdNoticia.append("sucursal", sucursalAnuncio);
        fdNoticia.append("documento_carpeta_id", id);
        await axios.post("/api/news", fdNoticia);
      }

      setPendingFile(null);
      setUploadForm(FORM_ARCHIVO_VACIO);
      setPublicarAnuncio(false);
      fetchArchivos();
    } catch (err) {
      console.error("Error al subir archivo:", err);
      setError(err?.response?.data?.error || "No se pudo subir el archivo.");
    } finally {
      setUploading(false);
    }
  };

  const handleOpenEditarArchivo = (archivo) => {
    setEditandoArchivo(archivo);
    setArchivoForm({ nombre: archivo.nombre, descripcion: archivo.descripcion || "" });
  };

  const handleGuardarArchivo = async (e) => {
    e.preventDefault();
    if (!archivoForm.nombre.trim()) return;
    setSaving(true);
    try {
      await axios.put(`/api/documentos/archivos/${editandoArchivo.id}`, {
        nombre: archivoForm.nombre.trim(),
        descripcion: archivoForm.descripcion.trim(),
      });
      setEditandoArchivo(null);
      fetchArchivos();
    } catch (err) {
      console.error("Error al actualizar archivo:", err);
      setError(err?.response?.data?.error || "No se pudo actualizar el archivo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (archivo) => {
    if (!window.confirm(`¿Eliminar "${archivo.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`/api/documentos/archivos/${archivo.id}`);
      fetchArchivos();
    } catch (err) {
      console.error("Error al eliminar archivo:", err);
      setError(err?.response?.data?.error || "No se pudo eliminar el archivo.");
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={carpeta?.nombre || "Carpeta"}
        subtitle="Documentos"
        backTo={carpeta?.padre_id ? `/documentos/${carpeta.padre_id}` : "/documentos"}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Editar nombre y descripción">
              <IconButton size="small" onClick={handleOpenEditar}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateNewFolderIcon />}
              onClick={handleOpenNuevaSubcarpeta}
            >
              Nueva subcarpeta
            </Button>
            <Button
              component="label"
              variant="contained"
              size="small"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "Subir archivo"}
              <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 820, mx: "auto" }}>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {carpeta && ruta.length > 0 && (
          <Breadcrumbs sx={{ mb: 1, fontSize: "0.85rem" }}>
            <Link component={RouterLink} to="/documentos" underline="hover" color="inherit">
              Documentos
            </Link>
            {ruta.map((p) => (
              <Link key={p.id} component={RouterLink} to={`/documentos/${p.id}`} underline="hover" color="inherit">
                {p.nombre}
              </Link>
            ))}
            <Typography color="text.primary" sx={{ fontSize: "inherit" }}>{carpeta.nombre}</Typography>
          </Breadcrumbs>
        )}

        {carpeta && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Creado por {carpeta.usuario} el {moment(carpeta.fecha).format("DD/MM/YYYY")}
          </Typography>
        )}

        {carpeta?.descripcion ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {carpeta.descripcion}
            </Typography>
          </Paper>
        ) : (
          carpeta && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={handleOpenEditar}
            >
              + Agregar una descripción para este tema
            </Typography>
          )
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {subcarpetas.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Subcarpetas</Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <List disablePadding>
                {subcarpetas.map((sub, idx) => {
                  const ocupada = sub.total_archivos > 0 || sub.total_subcarpetas > 0;
                  return (
                    <ListItem
                      key={sub.id}
                      disablePadding
                      divider={idx < subcarpetas.length - 1}
                      secondaryAction={
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton edge="end" onClick={() => handleOpenEditarSubcarpeta(sub)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={ocupada ? "No se puede eliminar: tiene archivos o subcarpetas dentro" : "Eliminar subcarpeta"}>
                            <span>
                              <IconButton edge="end" disabled={ocupada} onClick={() => handleEliminarSubcarpeta(sub)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemButton onClick={() => navigate(`/documentos/${sub.id}`)} sx={{ pr: 12 }}>
                        <ListItemIcon sx={{ minWidth: 56 }}>
                          <FolderIcon sx={{ color: "success.main", fontSize: 34 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={sub.nombre}
                          secondary={
                            plural(sub.total_archivos, "archivo") +
                            (sub.total_subcarpetas > 0 ? ` • ${plural(sub.total_subcarpetas, "subcarpeta")}` : "") +
                            (sub.descripcion ? ` • ${sub.descripcion}` : "")
                          }
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Archivos</Typography>
          </>
        )}

        {!loading && archivos.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {subcarpetas.length > 0
              ? "No hay archivos sueltos en esta carpeta."
              : "Esta carpeta no tiene archivos todavía."}
          </Typography>
        )}

        {!loading && archivos.length > 0 && (
          <Paper variant="outlined">
            <List disablePadding>
              {archivos.map((archivo, idx) => {
                const esAutor = archivo.usuario === user?.username || canManage;
                return (
                <ListItem
                  key={archivo.id}
                  divider={idx < archivos.length - 1}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {esAutor && (
                        <Tooltip title="Editar">
                          <IconButton edge="end" onClick={() => handleOpenEditarArchivo(archivo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Descargar">
                        <IconButton
                          edge="end"
                          component="a"
                          href={`${ENV.API_BASE_URL}${archivo.archivo_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {esAutor && (
                        <Tooltip title="Eliminar">
                          <IconButton edge="end" onClick={() => handleDelete(archivo)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemAvatar sx={{ mt: 0.5 }}>
                    <FileIcon nombre={archivo.nombre} url={archivo.archivo_url} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={archivo.nombre}
                    secondary={
                      <>
                        {archivo.descripcion && (
                          <Typography component="span" variant="body2" color="text.primary" sx={{ display: "block" }}>
                            {archivo.descripcion}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {archivo.usuario} • {moment(archivo.fecha).format("DD/MM/YYYY")} • {formatBytes(archivo.tamano)}
                        </Typography>
                      </>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>

      {/* Editar carpeta */}
      <Modal open={openEditar} onClose={() => setOpenEditar(false)}>
        <Paper
          component="form"
          onSubmit={handleGuardarEdicion}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 460, maxWidth: "90vw", p: 3, borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>Editar carpeta</Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Descripción"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              placeholder="De qué trata este tema, para orientar a quien lo abra..."
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setOpenEditar(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {/* Nueva / editar subcarpeta */}
      <Modal open={openSubcarpeta} onClose={() => setOpenSubcarpeta(false)}>
        <Paper
          component="form"
          onSubmit={handleGuardarSubcarpeta}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 460, maxWidth: "90vw", p: 3, borderRadius: 2,
          }}
        >
          <Typography variant="h6">{subForm.id ? "Editar subcarpeta" : "Nueva subcarpeta"}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dentro de "{carpeta?.nombre || ""}"
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre"
              value={subForm.nombre}
              onChange={(e) => setSubForm({ ...subForm, nombre: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Descripción"
              value={subForm.descripcion}
              onChange={(e) => setSubForm({ ...subForm, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              placeholder="Qué se guarda en esta subcarpeta..."
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setOpenSubcarpeta(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : subForm.id ? "Guardar cambios" : "Crear"}
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {/* Confirmar subida de archivo (nombre + descripción) */}
      <Modal open={Boolean(pendingFile)} onClose={() => (!uploading ? setPendingFile(null) : null)}>
        <Paper
          component="form"
          onSubmit={handleConfirmarSubida}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 460, maxWidth: "90vw", p: 3, borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>Subir archivo</Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre"
              value={uploadForm.nombre}
              onChange={(e) => setUploadForm({ ...uploadForm, nombre: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Descripción"
              value={uploadForm.descripcion}
              onChange={(e) => setUploadForm({ ...uploadForm, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              placeholder="De qué trata este archivo..."
            />

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={publicarAnuncio}
                  onChange={(e) => setPublicarAnuncio(e.target.checked)}
                />
              }
              label="Publicar también como anuncio en Noticias"
            />
            {publicarAnuncio && (
              <TextField
                select
                label="Sucursal del anuncio"
                value={sucursalAnuncio}
                onChange={(e) => setSucursalAnuncio(e.target.value)}
                fullWidth
                required
              >
                {SUCURSALES_NOTICIA.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setPendingFile(null)} disabled={uploading}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={uploading}>
              {uploading ? "Subiendo..." : "Subir"}
            </Button>
          </Stack>
        </Paper>
      </Modal>

      {/* Editar archivo existente */}
      <Modal open={Boolean(editandoArchivo)} onClose={() => setEditandoArchivo(null)}>
        <Paper
          component="form"
          onSubmit={handleGuardarArchivo}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 460, maxWidth: "90vw", p: 3, borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>Editar archivo</Typography>
          <Stack spacing={2}>
            <TextField
              label="Nombre"
              value={archivoForm.nombre}
              onChange={(e) => setArchivoForm({ ...archivoForm, nombre: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Descripción"
              value={archivoForm.descripcion}
              onChange={(e) => setArchivoForm({ ...archivoForm, descripcion: e.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setEditandoArchivo(null)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </DashboardLayout>
  );
};

export default CarpetaDetallePage;
